// CookieConsentBanner.tsx

import React from 'react';
import '../../Assets/styles/LandingStyles/CookieBanner/CookieBanner.css';
import { useConsent } from '../../Context/ConsentContext';

const CookieConsentBanner: React.FC = () => {
    const {
        confirmConsent,
        showBanner,
        toggleConsentDetail,
        analyticsDetails,
        advertisingDetails,
        partialConsent,
        toggleConsentSetting
    } = useConsent();

    if (!showBanner) return null;

    return (
        <>
            <div className="consent-overlay" />
            <div className="cookie-consent-banner">
                <div className="cookie-consent-container">
                    <div className="cookie-consent-header">
                        <h2 className="cookie-consent-title">Cookie Notice</h2>
                    </div>
                    <p className="cookie-consent-description">
                        We use cookies to enhance your Sudoku VS experience, analyze our traffic, and provide personalized ads.
                        You can select which cookies you want to allow before continuing.
                    </p>

                    <div className="consent-options">
                        <div className="consent-group">
                            <div className="consent-group-header">
                                <h3 className="consent-group-title">Analytics Consent</h3>
                                <button className="consent-group-toggle" onClick={() => toggleConsentDetail('analytics')}>
                                    {analyticsDetails ? 'Hide details' : 'Show details'}
                                </button>
                            </div>
                            <p className="consent-description">
                                These cookies help us analyze user behavior to improve our game and services.
                            </p>

                            {analyticsDetails && (
                                <div className="consent-item">
                                    <div className="consent-item-info">
                                        <h4 className="consent-item-title">Analytics cookies</h4>
                                        <p className="consent-item-description">
                                            Allows us to collect data about how you interact with Sudoku VS.
                                        </p>
                                    </div>
                                    <div className="consent-item-impact">
                                        <small>Impact: User reporting, Game improvements</small>
                                    </div>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={partialConsent.analytics_storage === 'granted'}
                                            onChange={() => toggleConsentSetting('analytics_storage')}
                                        />
                                        <span className="slider" />
                                    </label>
                                </div>
                            )}
                        </div>

                        <div className="consent-group">
                            <div className="consent-group-header">
                                <h3 className="consent-group-title">Advertising Consent</h3>
                                <button className="consent-group-toggle" onClick={() => toggleConsentDetail('advertising')}>
                                    {advertisingDetails ? 'Hide details' : 'Show details'}
                                </button>
                            </div>
                            <p className="consent-description">
                                These cookies help us deliver relevant ads and measure their performance.
                            </p>

                            {advertisingDetails && (
                                <>
                                    <div className="consent-item">
                                        <div className="consent-item-info">
                                            <h4 className="consent-item-title">Ad cookies</h4>
                                            <p className="consent-item-description">
                                                Allows the storage of advertising data on your device.
                                            </p>
                                        </div>
                                        <div className="consent-item-impact">
                                            <small>Impact: Ad measurement</small>
                                        </div>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={partialConsent.ad_storage === 'granted'}
                                                onChange={() => toggleConsentSetting('ad_storage')}
                                            />
                                            <span className="slider" />
                                        </label>
                                    </div>

                                    <div className="consent-item">
                                        <div className="consent-item-info">
                                            <h4 className="consent-item-title">Ad user data</h4>
                                            <p className="consent-item-description">
                                                Allows collection of data for advertising measurement.
                                            </p>
                                        </div>
                                        <div className="consent-item-impact">
                                            <small>Impact: Conversion tracking</small>
                                        </div>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={partialConsent.ad_user_data === 'granted'}
                                                onChange={() => toggleConsentSetting('ad_user_data')}
                                            />
                                            <span className="slider" />
                                        </label>
                                    </div>

                                    <div className="consent-item">
                                        <div className="consent-item-info">
                                            <h4 className="consent-item-title">Ad personalization</h4>
                                            <p className="consent-item-description">
                                                Allows us to show you personalized ads based on your interests.
                                            </p>
                                        </div>
                                        <div className="consent-item-impact">
                                            <small>Impact: Relevant advertising</small>
                                        </div>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={partialConsent.ad_personalization === 'granted'}
                                                onChange={() => toggleConsentSetting('ad_personalization')}
                                            />
                                            <span className="slider" />
                                        </label>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="btn-group">
                        <button
                            className="btn btn-accept-all"
                            onClick={confirmConsent}
                            data-testid="confirm-consent-button"
                        >
                            Confirm
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

declare global {
    interface Window {
        gtag: (command: string, action: string, params?: any) => void;
        gaTrackingReady?: boolean;
        dataLayer: any[];
    }
}

export default CookieConsentBanner;